import * as React from "react";
import { Button } from "@vicreturns/ui/button";
import { Checkbox } from "@vicreturns/ui/checkbox";
import { Input } from "@vicreturns/ui/input";
import { RadioGroup, RadioGroupItem } from "@vicreturns/ui/radio-group";
import "../types";

type Company = "individual" | "partner";

interface FormMessage {
  text: string;
  isError: boolean;
}

export function SubscribeForm() {
  const [company, setCompany] = React.useState<Company>("individual");
  const [email, setEmail] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState<FormMessage | null>(null);

  const config = window.subscribeFormConfig;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!config?.ajaxUrl || !config?.nonce) {
      console.error("Subscribe Form: Configuration not found");
      return;
    }

    setMessage(null);
    setSubmitting(true);

    const body = new URLSearchParams({
      action: "subscribe_form_submit",
      subscribe_form_nonce: config.nonce,
      company,
      email,
      consent: consent ? "on" : "",
    });

    try {
      const response = await fetch(config.ajaxUrl, {
        method: "POST",
        body,
        credentials: "same-origin",
      });

      const json = await response.json().catch(() => ({}));

      if (json.success && json.data?.message) {
        setMessage({ text: json.data.message, isError: false });
        setCompany("individual");
        setEmail("");
        setConsent(false);
      } else {
        const msg = json.data?.message ?? "Something went wrong. Please try again.";
        setMessage({ text: msg, isError: true });
      }
    } catch {
      setMessage({ text: "Unable to submit. Please try again later.", isError: true });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="subscribe-form flex flex-col gap-5"
      onSubmit={handleSubmit}
      noValidate
      aria-label="Newsletter subscription"
    >
      <fieldset
        className="flex flex-col md:flex-row gap-3 md:gap-5 border-0 p-0 m-0"
        aria-labelledby="company-legend"
      >
        <legend id="company-legend" className="sr-only">
          Company type
        </legend>
        <RadioGroup
          value={company}
          onValueChange={(value) => setCompany(value as Company)}
          className="flex flex-col md:flex-row gap-3 md:gap-5"
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="individual" id="subscribe-form-individual" required />
            <span className="text-foreground text-[15px] font-normal">
              I'm an individual
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="partner" id="subscribe-form-partner" required />
            <span className="text-foreground text-[15px] font-normal">
              I'm a donation partner
            </span>
          </label>
        </RadioGroup>
      </fieldset>

      <div className="group relative flex flex-row gap-2">
        <label htmlFor="subscribe-form-email" className="sr-only">
          Email address
        </label>
        <Input
          type="email"
          id="subscribe-form-email"
          placeholder="Enter email"
          name="email"
          className="peer flex w-full pr-14 text-primary [&:not(:placeholder-shown)]:bg-background"
          maxLength={80}
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Button
          type="submit"
          size="icon"
          variant="ghost"
          className="absolute right-[4px] top-1/2 -translate-y-1/2 w-[60px] h-[40px] text-foreground group-focus-within:text-primary peer-[:not(:placeholder-shown)]:text-primary hover:bg-transparent"
          aria-label="Subscribe"
          disabled={submitting}
          >
          <svg className="!size-5" width="30" height="24" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill="currentColor" d="M2 8C1.44772 8 1 8.44772 1 9C1 9.55228 1.44772 10 2 10L2 9L2 8ZM21.7071 9.70711C22.0976 9.31658 22.0976 8.68342 21.7071 8.29289L15.3431 1.92893C14.9526 1.53841 14.3195 1.53841 13.9289 1.92893C13.5384 2.31946 13.5384 2.95262 13.9289 3.34315L19.5858 9L13.9289 14.6569C13.5384 15.0474 13.5384 15.6805 13.9289 16.0711C14.3195 16.4616 14.9526 16.4616 15.3431 16.0711L21.7071 9.70711ZM2 9L2 10L21 10L21 9L21 8L2 8L2 9Z" />
          </svg>
        </Button>
      </div>

      <div className="flex flex-row gap-2">
        <label className="flex items-center gap-4 cursor-pointer text-sm text-brand-text">
          <Checkbox
            id="subscribe-form-consent"
            checked={consent}
            onCheckedChange={(checked) => setConsent(checked === true)}
            required
          />
          <span className="text-foreground text-[11px] leading-[17px] font-normal">
            By subscribing to this list, I agree to receive email communications from CDS
            Vic. CDS Vic will use the personal information I provide, such as my name and
            email address, in accordance with CDS Vic&rsquo;s Privacy Policy
          </span>
        </label>
      </div>

      <div
        className="flex flex-row items-center gap-2 text-sm font-normal text-foreground"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        hidden={!message}
      >
        {message && (
          <span className={message.isError ? "text-red-300" : undefined}>
            {message.text}
          </span>
        )}
      </div>
    </form>
  );
}
